/**
 * Aba Férias & afastamentos do funcionário (E10d — integração Convenia §3.8/§3.9):
 * somente leitura — os dados chegam pelo sync (domínios "ferias" e
 * "afastamentos"). O estoque mostra períodos aquisitivos com saldo DERIVADO
 * (direito − consumos; nunca gravado) confrontado com o balance do Convenia
 * (divergência = alerta de conferência), as solicitações (pedidos de gozo,
 * com vínculo ao recibo interno quando houver) e o ledger de afastamentos.
 * Sócio: estoque informativo (não gera cálculo).
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Card, Group, Table, Text, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { api } from "../../api";
import { fmtData } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";

interface Periodo {
  id: string;
  inicioAquisitivo: string;
  fimAquisitivo: string;
  inicioConcessivo: string | null;
  fimConcessivo: string | null;
  diasDireito: number;
  consumo: number;
  saldoDerivado: number;
  saldoConvenia: number | null;
  divergeDoConvenia: boolean | null;
  origem: string;
}
interface Solicitacao {
  id: string;
  inicioGozo: string;
  fimGozo: string | null;
  diasGozo: number;
  diasAbono: number;
  consumoDias: number | null;
  status: string;
  consomeSaldo: boolean;
  antecipacao13: boolean;
  enviadoContabilidade: boolean;
  justificativa: string | null;
  periodoAquisitivoId: string | null;
  processamentoId: string | null;
}
interface AlertaVencimento {
  periodoId: string;
  tipo: "vencida" | "a_vencer";
  fimConcessivo: string;
  saldoDerivado: number;
  diasParaLimite: number;
}
interface Estoque {
  tipoVinculo: string;
  /** 26/08/2026 — flag efetivo do "Permitir direito a férias" (false = aba só
   *  informativa, sem alertas de vencido — pedido do dono). */
  direitoFerias?: boolean;
  periodos: Periodo[];
  solicitacoes: Solicitacao[];
  alertasVencimento: AlertaVencimento[];
  divergencias: string[];
}
interface Afastamento {
  id: string;
  tipo: string | null;
  motivo: string | null;
  inicio: string;
  fim: string | null;
  totalDias: number | null;
  cid: string | null;
  justificativa: string | null;
  origem: string;
}

const ROTULO_STATUS: Record<string, string> = {
  approved: "aprovada",
  pending: "pendente",
  canceled: "cancelada",
  rejected: "rejeitada",
};

export function FeriasAfastamentosTab({ funcionarioId }: { funcionarioId: string }) {
  const [estoque, setEstoque] = useState<Estoque | null>(null);
  const [afastamentos, setAfastamentos] = useState<Afastamento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    Promise.all([
      api.get<Estoque>(`/funcionarios/${funcionarioId}/ferias-estoque`),
      api.get<{ afastamentos: Afastamento[] }>(`/funcionarios/${funcionarioId}/afastamentos`),
    ])
      .then(([e, a]) => {
        setEstoque(e);
        setAfastamentos(a.afastamentos);
      })
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!estoque || !afastamentos) return <Carregando linhas={4} />;

  return (
    <>
      {estoque.tipoVinculo === "socio" && (
        <Alert color="gray" mb="md">
          Estoque de férias de sócio — informativo (não gera cálculo de gozo no sistema).
        </Alert>
      )}
      {/* 26/08/2026 (pedido do dono) — flag "Permitir direito a férias" DESLIGADO:
          a aba fica só informativa; os alertas de vencido já vêm filtrados do
          estoque (a pessoa sai das listas/alertas de férias). */}
      {estoque.direitoFerias === false && (
        <Alert color="gray" mb="md">
          Esta pessoa está sem direito a férias (cadastro, aba Dados) — os períodos abaixo são só informativos e ela não
          aparece nas listas/alertas de férias.
        </Alert>
      )}
      {estoque.alertasVencimento.map((a) => (
        <Alert key={a.periodoId} color={a.tipo === "vencida" ? "red" : "yellow"} icon={<IconAlertTriangle size={16} />} mb="xs">
          {a.tipo === "vencida"
            ? `Férias VENCIDAS: ${a.saldoDerivado} dia(s) com limite concessivo em ${fmtData(a.fimConcessivo)} — período em dobro (CLT art. 137).`
            : `Férias a vencer: ${a.saldoDerivado} dia(s) com limite concessivo em ${fmtData(a.fimConcessivo)} (${a.diasParaLimite} dias).`}
        </Alert>
      ))}
      {estoque.divergencias.map((d, i) => (
        <Alert key={i} color="orange" variant="light" mb="xs">
          Conferência: {d}
        </Alert>
      ))}

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Períodos aquisitivos <Badge variant="light">{estoque.periodos.length}</Badge>
        </Title>
        {estoque.periodos.length === 0 ? (
          <Vazio texto="Nenhum período sincronizado — rode o sync Convenia (domínio ferias) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Aquisitivo</Th>
                <Th>Limite concessivo</Th>
                <Th num>Direito</Th>
                <Th num>Consumo</Th>
                <Th num>Saldo (derivado)</Th>
                <Th num>Saldo Convenia</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {estoque.periodos.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Text size="sm">
                      {fmtData(p.inicioAquisitivo)} → {fmtData(p.fimAquisitivo)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{p.fimConcessivo ? fmtData(p.fimConcessivo) : "—"}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{p.diasDireito}d</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{p.consumo}d</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Badge color={p.saldoDerivado > 0 ? "sillis" : "gray"} variant="light">
                      {p.saldoDerivado}d
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Group gap={4} justify="flex-end">
                      <Text size="sm" c="dimmed">
                        {p.saldoConvenia ?? "—"}d
                      </Text>
                      {p.divergeDoConvenia === true && (
                        <Badge size="xs" color="orange" variant="light">
                          diverge
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Solicitações de férias (Convenia) <Badge variant="light">{estoque.solicitacoes.length}</Badge>
        </Title>
        {estoque.solicitacoes.length === 0 ? (
          <Vazio texto="Nenhuma solicitação sincronizada." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Gozo</Th>
                <Th num>Dias</Th>
                <Th num>Abono</Th>
                <Th>Status</Th>
                <Th>Vínculos</Th>
                <Th>Justificativa</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {estoque.solicitacoes.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text size="sm">
                      {fmtData(s.inicioGozo)} {s.fimGozo ? `→ ${fmtData(s.fimGozo)}` : ""}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{s.diasGozo}d</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{s.diasAbono > 0 ? `${s.diasAbono}d` : "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={s.status === "approved" ? "sillis" : "gray"}>
                      {ROTULO_STATUS[s.status] ?? s.status}
                    </Badge>
                    {s.antecipacao13 && (
                      <Badge size="xs" variant="outline" color="gray" ml={4}>
                        13º
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {s.processamentoId && (
                        <Badge size="xs" color="sillis" variant="light">
                          recibo interno
                        </Badge>
                      )}
                      {!s.periodoAquisitivoId && s.status === "approved" && (
                        <Badge size="xs" color="orange" variant="light">
                          sem período
                        </Badge>
                      )}
                      {s.enviadoContabilidade && (
                        <Badge size="xs" variant="outline" color="gray">
                          contabilidade
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={2} maw={260}>
                      {s.justificativa ?? "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Afastamentos e faltas <Badge variant="light">{afastamentos.length}</Badge>
        </Title>
        {afastamentos.length === 0 ? (
          <Vazio texto="Nenhum afastamento sincronizado — o endpoint de absences está vazio na conta Convenia hoje." />
        ) : (
          <TabelaDados maw={820}>
            <Table.Thead>
              <Table.Tr>
                <Th>Período</Th>
                <Th>Tipo</Th>
                <Th>Motivo</Th>
                <Th num>Dias</Th>
                <Th>CID</Th>
                <Th>Justificativa</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {afastamentos.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Text size="sm">
                      {fmtData(a.inicio)} {a.fim ? `→ ${fmtData(a.fim)}` : ""}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" color="gray">
                      {a.tipo ?? "—"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{a.motivo ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{a.totalDias ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{a.cid ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{a.justificativa ?? "—"}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
      <Group>
        <Text size="xs" c="dimmed">
          Dados somente leitura — a fonte é o Convenia. O saldo é derivado (direito − consumos das solicitações aprovadas
          e dos recibos internos não vinculados); o saldo do Convenia é só conferência.
        </Text>
      </Group>
    </>
  );
}
